/**
 * ScheduleMessagePopover — Programma l'invio di un messaggio
 * Notion-style design, pannello sopra il bottone invio
 */

import React, { useState } from 'react';
import { Clock, X } from 'lucide-react';

interface ScheduleMessagePopoverProps {
  onSchedule: (date: Date) => void;
  onClose: () => void;
}

function getNextWeekdayAt(dayOffset: number, hour: number, minute = 0): Date {
  const now = new Date();
  const target = new Date(now);
  target.setDate(now.getDate() + dayOffset);
  target.setHours(hour, minute, 0, 0);
  return target;
}

function getNextMondayAt9(): Date {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon...
  const daysUntilMonday = day === 0 ? 1 : day === 1 ? 7 : 8 - day;
  const target = new Date(now);
  target.setDate(now.getDate() + daysUntilMonday);
  target.setHours(9, 0, 0, 0);
  return target;
}

const formatDateTime = (d: Date) =>
  d.toLocaleString('it-IT', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

export function ScheduleMessagePopover({ onSchedule, onClose }: ScheduleMessagePopoverProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('09:00');

  const presets = [
    { label: 'Tra 1 ora', date: new Date(Date.now() + 3600000) },
    { label: 'Domani mattina (9:00)', date: getNextWeekdayAt(1, 9) },
    { label: 'Lunedi mattina (9:00)', date: getNextMondayAt9() },
  ];

  const handleCustom = () => {
    if (!customDate || !customTime) return;
    const [y, m, d] = customDate.split('-').map(Number);
    const [h, min] = customTime.split(':').map(Number);
    const target = new Date(y, m - 1, d, h, min, 0, 0);
    if (target <= new Date()) return;
    onSchedule(target);
  };

  return (
    <div
      className="absolute bottom-full mb-2 right-0 z-50 w-72 bg-white rounded-xl shadow-2xl overflow-hidden"
      style={{ border: '1px solid rgba(55,53,47,0.12)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
        <div className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" style={{ color: '#2383E2' }} strokeWidth={1.5} />
          <span className="text-[13px] font-semibold" style={{ color: '#37352F' }}>Programma invio</span>
        </div>
        <button onClick={onClose} className="p-0.5 rounded hover:bg-[rgba(55,53,47,0.06)]">
          <X className="w-3.5 h-3.5" style={{ color: '#787774' }} strokeWidth={1.5} />
        </button>
      </div>

      {/* Presets */}
      <div className="px-2 py-1.5 space-y-0.5">
        {presets.map(p => (
          <button
            key={p.label}
            onClick={() => onSchedule(p.date)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-[12px] transition-colors"
            style={{ color: '#37352F' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(55,53,47,0.06)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <span className="font-medium">{p.label}</span>
            <span className="text-[10px]" style={{ color: '#9B9A97' }}>{formatDateTime(p.date)}</span>
          </button>
        ))}

        {/* Custom toggle */}
        <button
          onClick={() => setShowCustom(o => !o)}
          className="w-full flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[12px] font-medium transition-colors"
          style={{ color: '#2383E2' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(35,131,226,0.06)')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <Clock className="w-3 h-3" strokeWidth={1.5} />
          Data e ora personalizzata
        </button>
      </div>

      {/* Custom date/time picker */}
      {showCustom && (
        <div className="px-3 pb-3 space-y-2" style={{ borderTop: '1px solid rgba(55,53,47,0.06)' }}>
          <div className="pt-2 flex items-center gap-2">
            <input
              type="date"
              value={customDate}
              onChange={e => setCustomDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="flex-1 px-2 py-1.5 text-[12px] rounded-md outline-none"
              style={{ backgroundColor: 'rgba(55,53,47,0.06)', color: '#37352F', border: '1px solid transparent' }}
              onFocus={e => (e.target.style.borderColor = 'rgba(35,131,226,0.4)')}
              onBlur={e => (e.target.style.borderColor = 'transparent')}
            />
            <input
              type="time"
              value={customTime}
              onChange={e => setCustomTime(e.target.value)}
              className="w-24 px-2 py-1.5 text-[12px] rounded-md outline-none"
              style={{ backgroundColor: 'rgba(55,53,47,0.06)', color: '#37352F', border: '1px solid transparent' }}
              onFocus={e => (e.target.style.borderColor = 'rgba(35,131,226,0.4)')}
              onBlur={e => (e.target.style.borderColor = 'transparent')}
            />
          </div>
          <button
            onClick={handleCustom}
            disabled={!customDate || !customTime}
            className="w-full py-1.5 rounded-lg text-[12px] font-medium text-white transition-colors disabled:opacity-50"
            style={{ backgroundColor: '#2383E2' }}
          >
            Programma
          </button>
        </div>
      )}
    </div>
  );
}
